/**
 * Tax and bank details (owner only): PAN, GSTIN, Udyam, address — what goes
 * on the payment advice and decides TDS — and where the money is sent. The
 * full account number is never shown again once saved, only its last four
 * digits.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  ActionBar,
  Card,
  ErrorNote,
  Field,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

interface TaxResponse {
  businessName: string;
  legalName: string | null;
  entityType: string | null;
  pan: string | null;
  gstin: string | null;
  udyamNumber: string | null;
  address: string | null;
  pincode: string | null;
  state: string | null;
  upiId: string | null;
  bank: { holder: string | null; ifsc: string | null; last4: string } | null;
  updatedAt: string | null;
  states: string[];
}

const ENTITY: Array<[string, string, string]> = [
  ['proprietorship', 'प्रोपराइटरशिप (अकेले मालिक)', 'Sole proprietorship'],
  ['individual', 'व्यक्तिगत', 'Individual'],
  ['partnership', 'पार्टनरशिप फ़र्म', 'Partnership firm'],
  ['llp', 'LLP', 'LLP'],
  ['private_limited', 'प्राइवेट लिमिटेड', 'Private limited company'],
  ['public_limited', 'पब्लिक लिमिटेड', 'Public limited company'],
  ['other', 'अन्य', 'Other'],
];
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const clean = (s: string) => s.replace(/[\s-]/g, '').toUpperCase();

export function TaxBankPage() {
  const T = useT();
  const { refresh, login } = useSession();
  const [data, setData] = useState<TaxResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState({
    legalName: '',
    entityType: '',
    pan: '',
    gstin: '',
    udyamNumber: '',
    address: '',
    pincode: '',
    state: '',
    upiId: '',
  });
  const [bank, setBank] = useState({ holder: '', ifsc: '', accountNumber: '', confirm: '' });
  const [editBank, setEditBank] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = () =>
    api.get<TaxResponse>('/v1/partner/tax').then(
      (r) => {
        setData(r);
        setF({
          legalName: r.legalName ?? '',
          entityType: r.entityType ?? '',
          pan: r.pan ?? '',
          gstin: r.gstin ?? '',
          udyamNumber: r.udyamNumber ?? '',
          address: r.address ?? '',
          pincode: r.pincode ?? '',
          state: r.state ?? '',
          upiId: r.upiId ?? '',
        });
        setBank({
          holder: r.bank?.holder ?? '',
          ifsc: r.bank?.ifsc ?? '',
          accountNumber: '',
          confirm: '',
        });
        setEditBank(!r.bank);
      },
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);

  if (login && login.role !== 'owner')
    return (
      <div>
        <SubHeader icon="shield" title={T('टैक्स और बैंक', 'Tax & bank')} />
        <Card>
          <p className="text-sm text-muted">
            {T(
              'टैक्स और बैंक की जानकारी सिर्फ़ मालिक देख सकते हैं।',
              'Only the business owner sees tax and bank details.',
            )}
          </p>
        </Card>
      </div>
    );
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;

  const pan = clean(f.pan);
  const gstin = clean(f.gstin);
  const hint = {
    pan:
      pan && !PAN_RE.test(pan)
        ? T('PAN ऐसा होता है: ABCDE1234F', 'A PAN looks like ABCDE1234F.')
        : null,
    gstin:
      gstin && !GSTIN_RE.test(gstin)
        ? T('GSTIN ऐसा होता है: 09ABCDE1234F1Z5', 'A GSTIN looks like 09ABCDE1234F1Z5.')
        : gstin && PAN_RE.test(pan) && gstin.slice(2, 12) !== pan
          ? T('GSTIN में आपका PAN नहीं है।', 'This GSTIN doesn’t contain your PAN.')
          : null,
    ifsc:
      editBank && bank.ifsc && !IFSC_RE.test(clean(bank.ifsc))
        ? T('IFSC ऐसा होता है: SBIN0001234', 'An IFSC looks like SBIN0001234.')
        : null,
    account:
      editBank && bank.accountNumber && bank.confirm && bank.accountNumber !== bank.confirm
        ? T('दोनों खाता नंबर एक जैसे नहीं हैं।', 'The two account numbers don’t match.')
        : null,
  };
  const bankReady =
    !editBank ||
    (!bank.holder && !bank.ifsc && !bank.accountNumber) ||
    (bank.holder.trim().length >= 2 &&
      IFSC_RE.test(clean(bank.ifsc)) &&
      /^[0-9]{9,18}$/.test(bank.accountNumber) &&
      bank.accountNumber === bank.confirm);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const sendBank = editBank && bank.accountNumber;
      await api.post('/v1/partner/tax', {
        ...f,
        entityType: f.entityType || null,
        ...(sendBank
          ? {
              bank: {
                holder: bank.holder,
                ifsc: clean(bank.ifsc),
                accountNumber: bank.accountNumber,
              },
            }
          : {}),
      });
      await Promise.all([load(), refresh()]);
      setMsg({ text: T('सेव हो गया ✓', 'Saved ✓'), ok: true });
    } catch (e) {
      setMsg({
        text: e instanceof ApiError ? e.message : T('सेव नहीं हुआ।', 'Not saved.'),
        ok: false,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SubHeader
        icon="shield"
        title={T('टैक्स और बैंक', 'Tax & bank')}
        sub={T(
          'PAN देने पर TDS 1–2% कटता है, नहीं तो 20%। पैसा इसी खाते में आएगा।',
          'With a PAN, TDS is 1–2%; without, 20%. Payments go to this account.',
        )}
      />

      <Card className="space-y-4">
        <h2 className="font-display text-lg font-bold">{T('टैक्स', 'Tax')}</h2>
        <Field label={T('कानूनी नाम (PAN/GST पर जैसा)', 'Legal name (as on PAN / GST)')}>
          <input
            value={f.legalName}
            onChange={(e) => setF({ ...f, legalName: e.target.value })}
          />
        </Field>
        <Field label={T('बिज़नेस का प्रकार', 'Type of business')}>
          <select
            value={f.entityType}
            onChange={(e) => setF({ ...f, entityType: e.target.value })}
          >
            <option value="">{T('चुनें…', 'Choose…')}</option>
            {ENTITY.map(([k, hi, en]) => (
              <option key={k} value={k}>
                {T(hi, en)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="PAN" error={hint.pan}>
          <input
            className="tnum font-mono uppercase tracking-wider"
            maxLength={12}
            placeholder="ABCDE1234F"
            value={f.pan}
            onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label={T('GSTIN (अगर है)', 'GSTIN (if registered)')} error={hint.gstin}>
          <input
            className="tnum font-mono uppercase tracking-wider"
            maxLength={17}
            placeholder="09ABCDE1234F1Z5"
            value={f.gstin}
            onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label={T('उद्यम (MSME) नंबर — ज़रूरी नहीं', 'Udyam (MSME) number — optional')}>
          <input
            className="font-mono uppercase"
            placeholder="UDYAM-UP-09-0012345"
            value={f.udyamNumber}
            onChange={(e) => setF({ ...f, udyamNumber: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label={T('पता', 'Address')}>
          <textarea
            rows={2}
            value={f.address}
            onChange={(e) => setF({ ...f, address: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={T('पिन कोड', 'PIN code')}>
            <input
              inputMode="numeric"
              className="tnum"
              maxLength={6}
              value={f.pincode}
              onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, '') })}
            />
          </Field>
          <Field label={T('राज्य', 'State')}>
            <select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}>
              <option value="">{T('चुनें…', 'Choose…')}</option>
              {data.states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-display text-lg font-bold">
          {T('पैसा कहाँ भेजें', 'Where we pay you')}
        </h2>
        <Field label="UPI ID" hint={T('जैसे: 9876543210@ybl', 'e.g. 9876543210@ybl')}>
          <input
            value={f.upiId}
            autoCapitalize="none"
            onChange={(e) => setF({ ...f, upiId: e.target.value.trim() })}
          />
        </Field>
        {!editBank && data.bank ? (
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-panel-2 p-4">
            <div className="flex items-center gap-3">
              <span className="icon-tile h-10 w-10">
                <Icon name="shield" className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold">{data.bank.holder}</p>
                <p className="tnum text-sm text-muted">
                  •••• {data.bank.last4} · {data.bank.ifsc}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-quiet text-sm"
              onClick={() => setEditBank(true)}
            >
              {T('बदलें', 'Change')}
            </button>
          </div>
        ) : (
          <>
            <Field label={T('खाताधारक का नाम', 'Account holder name')}>
              <input
                value={bank.holder}
                onChange={(e) => setBank({ ...bank, holder: e.target.value })}
              />
            </Field>
            <Field label="IFSC" error={hint.ifsc}>
              <input
                className="tnum font-mono uppercase"
                maxLength={11}
                placeholder="SBIN0001234"
                value={bank.ifsc}
                onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label={T('खाता नंबर', 'Account number')}>
              <input
                inputMode="numeric"
                className="tnum"
                autoComplete="off"
                value={bank.accountNumber}
                onChange={(e) =>
                  setBank({
                    ...bank,
                    accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18),
                  })
                }
              />
            </Field>
            <Field label={T('खाता नंबर दोबारा', 'Account number again')} error={hint.account}>
              <input
                inputMode="numeric"
                className="tnum"
                autoComplete="off"
                value={bank.confirm}
                onChange={(e) =>
                  setBank({ ...bank, confirm: e.target.value.replace(/\D/g, '').slice(0, 18) })
                }
              />
            </Field>
            {data.bank && (
              <button
                type="button"
                className="btn-quiet text-sm"
                onClick={() => setEditBank(false)}
              >
                {T('पुराना खाता रखें', 'Keep the saved account')}
              </button>
            )}
          </>
        )}
        <p className="flex items-start gap-2 text-xs text-muted">
          <Icon name="shield" className="mt-0.5 h-4 w-4 shrink-0" />
          {T(
            'खाता नंबर ताले में (एन्क्रिप्ट) रखा जाता है; ऐप में सिर्फ़ आख़िरी 4 अंक दिखते हैं।',
            'The account number is stored encrypted; the app only ever shows its last 4 digits.',
          )}
        </p>
      </Card>

      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-good' : 'text-bad'}`} role="status">
          {msg.text}
        </p>
      )}
      <ActionBar>
        <button
          type="button"
          className="btn-primary"
          disabled={
            busy || Boolean(hint.pan || hint.gstin || hint.ifsc || hint.account) || !bankReady
          }
          onClick={() => void save()}
        >
          {busy ? T('सेव हो रहा है…', 'Saving…') : T('सेव करें', 'Save')}
        </button>
      </ActionBar>
    </div>
  );
}
